import React, { ReactNode } from 'react';
import styled from '@emotion/styled';
import EmailIcon from './Icons/Email';
import PhoneIcon from './Icons/Phone';
import ScheduleIcon from './Icons/Schedule';
import LocationIcon from './Icons/MapPin';
import { Breadcrumbs } from './Breadcrumb';
import { Grid, GridCell } from './Grid';
import InnerHtmlStyling from './InnerHtmlStyling';
import breakpoints from './Theme/breakpoints';
import grey from './Palettes/grey';

export interface ContactPageDepartment {
  id: string;
  label: ReactNode;
  subtitle?: ReactNode;
  icon?: ReactNode;
  email: string;
  phone?: string;
}

export interface ContactPageImpressum {
  title: string;
}

export interface ContactPagePerson {
  label: string;
  name: string;
}

export interface ContactPageCode {
  label: string;
  value: string;
}

export interface ContactPageCompanyCard {
  id: string;
  title: string;
  entity?: string;
  addressLines?: string[];
  email?: string;
  phone?: string;
  phoneNote?: string;
  people?: ContactPagePerson[];
  codes: ContactPageCode[];
}

export interface ContactPageCompanyInfo {
  title: string;
  cards: ContactPageCompanyCard[];
}

interface ContactPageProperties {
  breadcrumbs: ReactNode[];
  title: string;
  lead: string;
  generalPhoneNumber: string;
  generalPhoneDescription: string;
  hoursLabel: string;
  addressLabel: string;
  departmentsTitle: string;
  departmentsLead: string;
  departments: ContactPageDepartment[];
  noteLabel: string;
  disclaimer: ReactNode;
  impressum?: ContactPageImpressum;
  faqEyebrow: string;
  faqTitle: string;
  faqLead: string;
  faq: ReactNode;
  legalEyebrow: string;
  companyInfo: ContactPageCompanyInfo;
}

const PageWrapper = styled.div`
  margin: 0 auto 32px;
  max-width: 970px;
  box-sizing: border-box;
  width: 100%;
  padding: 0 16px;

  @media (min-width: ${breakpoints.lg}px) {
    margin: 0 auto 48px;
    padding: 0;
  }
`;

const BreadcrumbsWrapper = styled.div`
  display: flex;
  justify-content: start;
  flex-basis: 100%;
  padding-bottom: 2rem;
`;

const ContactPageWrapper = styled.div`
  text-align: left;
  font-family: ${({ theme }) => theme.fonts.primary};

  .eyebrow {
    display: inline-block;
    text-transform: uppercase;
    letter-spacing: 0.15em;
    font-size: 0.6875rem;
    font-weight: 700;
    color: ${({ theme }) => theme.palette.primary.main};
    margin: 0 0 16px;
  }

  .hero {
    margin: 0 0 56px;
  }

  .hero-title {
    margin: 0 0 24px;
    font-size: 2.25rem;
    line-height: 1.05;
    font-weight: 800;
    letter-spacing: -0.02em;
    color: ${({ theme }) => theme.palette.text.primary};
  }

  @media (min-width: ${breakpoints.md}px) {
    .hero-title {
      font-size: 3.25rem;
    }
  }

  .hero-lead {
    margin: 0 0 32px;
    font-size: 1.125rem;
    line-height: 1.75;
    color: ${({ theme }) => theme.palette.text.secondary};
    max-width: 720px;
  }

  .hero-meta {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 12px 20px;
    padding-bottom: 24px;
    border-bottom: 1px solid ${grey[300]};
  }

  .meta-item {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 0.9375rem;
    line-height: 1.5;
    color: ${({ theme }) => theme.palette.text.primary};
  }

  .meta-item svg {
    font-size: 18px;
    color: ${({ theme }) => theme.palette.text.secondary};
    flex-shrink: 0;
  }

  .meta-item a {
    color: inherit;
    text-decoration: none;
    font-weight: 700;
  }

  .meta-item .description {
    color: ${({ theme }) => theme.palette.text.secondary};
  }

  .meta-divider {
    display: none;
    width: 1px;
    height: 16px;
    background: ${grey[300]};
  }

  @media (min-width: ${breakpoints.md}px) {
    .meta-divider {
      display: inline-block;
    }
  }

  .section {
    margin: 0 0 56px;
  }

  .section-heading {
    margin: 0 0 16px;
    font-size: 1.75rem;
    line-height: 1.15;
    font-weight: 800;
    letter-spacing: -0.01em;
    color: ${({ theme }) => theme.palette.text.primary};
  }

  @media (min-width: ${breakpoints.md}px) {
    .section-heading {
      font-size: 2.25rem;
    }
  }

  .section-lead {
    margin: 0 0 32px;
    font-size: 1rem;
    line-height: 1.6;
    color: ${({ theme }) => theme.palette.text.secondary};
    max-width: 720px;
  }

  .department-grid {
    list-style: none;
    margin: 0;
    padding: 0;
    display: grid;
    grid-template-columns: 1fr;
    gap: 16px;
  }

  @media (min-width: ${breakpoints.sm}px) {
    .department-grid {
      grid-template-columns: repeat(2, 1fr);
    }
  }

  @media (min-width: ${breakpoints.md}px) {
    .department-grid {
      grid-template-columns: repeat(3, 1fr);
    }
  }

  .department-tile {
    background: #ffffff;
    border: 1px solid ${grey[300]};
    border-radius: 8px;
    padding: 24px;
  }

  .department-header {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    margin-bottom: 16px;
  }

  .department-icon-square {
    flex-shrink: 0;
    width: 40px;
    height: 40px;
    border-radius: 8px;
    background: #111111;
    color: #ffffff;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  .department-icon-square svg {
    font-size: 20px;
    color: #ffffff;
  }

  .department-text {
    min-width: 0;
  }

  .department-title {
    margin: 0;
    font-size: 1rem;
    line-height: 1.4;
    font-weight: 700;
    color: ${({ theme }) => theme.palette.text.primary};
  }

  .department-title a {
    font: inherit;
    color: inherit;
    text-decoration: none;
    border-bottom: 1px solid ${grey[400]};
  }

  .department-subtitle {
    margin: 4px 0 0;
    font-size: 0.875rem;
    line-height: 1.4;
    color: ${({ theme }) => theme.palette.text.secondary};
  }

  .department-divider {
    border: 0;
    height: 1px;
    background: ${grey[200]};
    margin: 16px 0;
  }

  .department-contact {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 0.9375rem;
    line-height: 1.5;
  }

  .department-contact + .department-contact {
    margin-top: 8px;
  }

  .department-contact svg {
    font-size: 18px;
    color: ${({ theme }) => theme.palette.primary.main};
    flex-shrink: 0;
  }

  .department-contact a {
    text-decoration: none;
    word-break: break-word;
    color: ${({ theme }) => theme.palette.primary.main};
    font-weight: 700;
  }

  .department-contact .phone-text {
    color: ${({ theme }) => theme.palette.text.primary};
    font-weight: 700;
  }

  .visually-hidden {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }

  a:focus-visible {
    outline: 2px solid ${({ theme }) => theme.palette.primary.main};
    outline-offset: 2px;
    border-radius: 2px;
  }

  @media (hover: hover) {
    .department-title a:hover {
      border-bottom-color: ${({ theme }) => theme.palette.primary.main};
    }

    .department-contact a:hover {
      text-decoration: underline;
    }

    .meta-item a:hover {
      color: ${({ theme }) => theme.palette.primary.main};
    }
  }

  .note-alert {
    margin: 0 0 56px;
    padding: 16px 20px;
    background: #ffffff;
    border: 1px solid ${grey[300]};
    border-left: 3px solid ${({ theme }) => theme.palette.primary.main};
    border-radius: 4px;
    font-size: 0.9375rem;
    line-height: 1.6;
    color: ${({ theme }) => theme.palette.text.secondary};
  }

  .impressum {
    margin: 0 0 56px;
    text-align: left;
  }

  .impressum h2 {
    margin: 0 0 16px;
    font-size: 1.75rem;
    line-height: 1.15;
    font-weight: 800;
    letter-spacing: -0.01em;
  }

  @media (min-width: ${breakpoints.md}px) {
    .impressum h2 {
      font-size: 2.25rem;
    }
  }

  .impressum p {
    font-size: 0.9375rem;
    line-height: 1.6;
    margin: 0 0 12px;
  }

  .faq-section {
    margin: 0 0 56px;
  }

  .faq-grid {
    display: grid;
    gap: 32px;
    grid-template-columns: 1fr;
  }

  @media (min-width: ${breakpoints.md}px) {
    .faq-grid {
      grid-template-columns: minmax(0, 1fr) minmax(0, 2fr);
      gap: 48px;
    }
  }

  .faq-header .section-lead {
    margin-bottom: 0;
  }

  .company-grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 16px;
  }

  @media (min-width: ${breakpoints.md}px) {
    .company-grid {
      grid-template-columns: repeat(2, 1fr);
      gap: 24px;
    }
  }

  .company-card {
    background: #ffffff;
    border: 1px solid ${grey[300]};
    border-radius: 8px;
    padding: 24px;
  }

  .company-card h3 {
    margin: 0 0 4px;
    padding: 0;
    font-size: 1.125rem;
    line-height: 1.3;
    font-weight: 700;
  }

  .company-card .company-entity {
    margin: 0 0 16px;
    font-size: 0.9375rem;
    color: ${({ theme }) => theme.palette.text.secondary};
  }

  .company-row {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    font-size: 0.9375rem;
    line-height: 1.5;
    margin: 0 0 8px;
  }

  .company-row svg {
    font-size: 18px;
    color: ${({ theme }) => theme.palette.text.secondary};
    flex-shrink: 0;
    margin-top: 2px;
  }

  .company-row a {
    color: ${({ theme }) => theme.palette.primary.main};
    text-decoration: none;
    font-weight: 700;
    word-break: break-word;
  }

  .company-row em {
    font-style: italic;
    color: ${({ theme }) => theme.palette.text.secondary};
    font-size: 0.8125rem;
  }

  .company-divider {
    border: 0;
    height: 1px;
    background: ${grey[200]};
    margin: 16px 0;
  }

  .company-codes {
    display: grid;
    grid-template-columns: max-content 1fr;
    column-gap: 16px;
    row-gap: 4px;
    font-size: 0.8125rem;
    line-height: 1.5;
  }

  .company-codes dt {
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: ${({ theme }) => theme.palette.text.secondary};
  }

  .company-codes dd {
    margin: 0;
    text-align: right;
    font-variant-numeric: tabular-nums;
  }

  .company-people {
    display: grid;
    grid-template-columns: max-content 1fr;
    column-gap: 16px;
    row-gap: 4px;
    font-size: 0.875rem;
    line-height: 1.5;
    margin: 0 0 16px;
  }

  .company-people dt {
    color: ${({ theme }) => theme.palette.text.secondary};
  }

  .company-people dd {
    margin: 0;
    text-align: right;
    font-weight: 700;
  }
`;

const DisclaimerWrapper = styled(InnerHtmlStyling)`
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid ${grey[200]};
  font-size: 0.8125rem;
  line-height: 1.6;
  color: ${({ theme }) => theme.palette.text.secondary};
  text-align: center;

  p {
    margin: 0;
  }
`;

const renderImpressum = (
  impressum: ContactPageImpressum,
  generalPhoneNumber: string,
) => (
  <section className="impressum">
    <h2>{impressum.title}</h2>
    <p>
      <strong>Angaben gemäß § 5 TMG</strong>
    </p>
    <p>
      Infonomy BV
      <br />
      Hoogoorddreef 56S
      <br />
      1101 BE Amsterdam, Niederlande
    </p>
    <p>
      <strong>Vertreten durch:</strong>
      <br />
      L.A. Minderhoud (CEO)
    </p>
    <p>
      <strong>Kontakt</strong>
      <br />
      Telefon:
      {' '}
      <a href={`tel:${generalPhoneNumber}`}>{generalPhoneNumber}</a>
      <br />
      E-Mail:
      {' '}
      <a href="mailto:media@fashionunited.com">media@fashionunited.com</a>
    </p>
    <p>
      <strong>Registereintrag</strong>
      <br />
      Handelsregister Amsterdam (KvK): 32078229
      <br />
      Umsatzsteuer-Identifikationsnummer (§ 27a UStG):
      {' '}
      NL808503558B01
    </p>
    <p>
      <strong>
        Verantwortlich für den Inhalt nach § 18 Abs. 2 MStV
      </strong>
      <br />
      E. van Loon (Chefredakteurin)
      <br />
      Hoogoorddreef 56S
      <br />
      1101 BE Amsterdam, Niederlande
    </p>
  </section>
);

const renderCompanyCard = (card: ContactPageCompanyCard) => (
  <div className="company-card" key={card.id}>
    <h3>{card.title}</h3>
    {card.entity && <p className="company-entity">{card.entity}</p>}
    {card.addressLines && (
      <div className="company-row">
        <LocationIcon aria-hidden />
        <span>
          {card.addressLines.map((line, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <React.Fragment key={index}>
              {index > 0 && <br />}
              {line}
            </React.Fragment>
          ))}
        </span>
      </div>
    )}
    {card.email && (
      <div className="company-row">
        <EmailIcon aria-hidden />
        <a href={`mailto:${card.email}`}>{card.email}</a>
      </div>
    )}
    {card.phone && (
      <div className="company-row">
        <PhoneIcon aria-hidden />
        <a href={`tel:${card.phone}`}>{card.phone}</a>
      </div>
    )}
    {card.phoneNote && (
      <div className="company-row">
        <em>{card.phoneNote}</em>
      </div>
    )}
    <hr className="company-divider" />
    {card.people && (
      <dl className="company-people">
        {card.people.map((person) => (
          <React.Fragment key={person.label}>
            <dt>{person.label}</dt>
            <dd>{person.name}</dd>
          </React.Fragment>
        ))}
      </dl>
    )}
    <dl className="company-codes">
      {card.codes.map((code) => (
        <React.Fragment key={code.label}>
          <dt>{code.label}</dt>
          <dd>{code.value}</dd>
        </React.Fragment>
      ))}
    </dl>
  </div>
);

const ContactPage = ({
  breadcrumbs,
  title,
  lead,
  generalPhoneNumber,
  generalPhoneDescription,
  hoursLabel,
  addressLabel,
  departmentsTitle,
  departmentsLead,
  departments,
  noteLabel,
  disclaimer,
  impressum,
  faqEyebrow,
  faqTitle,
  faqLead,
  faq,
  legalEyebrow,
  companyInfo,
}: ContactPageProperties) => (
  <Grid>
    <GridCell padding="0">
      <PageWrapper>
        <BreadcrumbsWrapper>
          <Breadcrumbs crumbs={breadcrumbs} />
        </BreadcrumbsWrapper>
        <ContactPageWrapper>
          <section className="hero">
            <h1 className="hero-title">{title}</h1>
            <p className="hero-lead">{lead}</p>
            <div className="hero-meta">
              <span className="meta-item">
                <PhoneIcon aria-hidden />
                <a href={`tel:${generalPhoneNumber}`}>{generalPhoneNumber}</a>
                <span className="description">
                  {' · '}
                  {generalPhoneDescription}
                </span>
              </span>
              <span className="meta-divider" aria-hidden />
              <span className="meta-item">
                <ScheduleIcon aria-hidden />
                <span>{hoursLabel}</span>
              </span>
              <span className="meta-divider" aria-hidden />
              <span className="meta-item">
                <LocationIcon aria-hidden />
                <span>{addressLabel}</span>
              </span>
            </div>
          </section>

          <section className="section">
            <h2 className="section-heading">{departmentsTitle}</h2>
            <p className="section-lead">{departmentsLead}</p>
            <ul className="department-grid">
              {departments.map((department) => (
                <li className="department-tile" key={department.id}>
                  <div className="department-header">
                    {department.icon && (
                      <span className="department-icon-square" aria-hidden>
                        {department.icon}
                      </span>
                    )}
                    <div className="department-text">
                      <h3 className="department-title">{department.label}</h3>
                      {department.subtitle && (
                        <p className="department-subtitle">
                          {department.subtitle}
                        </p>
                      )}
                    </div>
                  </div>
                  <hr className="department-divider" />
                  <div className="department-contact">
                    <EmailIcon aria-hidden />
                    <a href={`mailto:${department.email}`}>
                      <span className="visually-hidden">Email: </span>
                      {department.email}
                    </a>
                  </div>
                  {department.phone && (
                    <div className="department-contact">
                      <PhoneIcon aria-hidden />
                      <a
                        className="phone-text"
                        href={`tel:${department.phone}`}
                      >
                        <span className="visually-hidden">Phone: </span>
                        {department.phone}
                      </a>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </section>

          <p className="note-alert">{noteLabel}</p>

          {impressum && renderImpressum(impressum, generalPhoneNumber)}

          <section className="faq-section">
            <div className="faq-grid">
              <div className="faq-header">
                <span className="eyebrow">{faqEyebrow}</span>
                <h2 className="section-heading">{faqTitle}</h2>
                <p className="section-lead">{faqLead}</p>
              </div>
              <div className="faq-content">{faq}</div>
            </div>
          </section>

          <section className="section">
            <span className="eyebrow">{legalEyebrow}</span>
            <h2 className="section-heading">{companyInfo.title}</h2>
            <div className="company-grid">
              {companyInfo.cards.map((card) => renderCompanyCard(card))}
            </div>
          </section>

          <DisclaimerWrapper>{disclaimer}</DisclaimerWrapper>
        </ContactPageWrapper>
      </PageWrapper>
    </GridCell>
  </Grid>
);

export default ContactPage;
